import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CubeBox } from '../ui/CountingCubes';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X } from 'lucide-react';

const MatchGroupToNumber = ({ exercise, onAnswer }) => {
  const { count, options } = exercise;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  }, [exercise]);

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    const correct = answer === count;
    setIsCorrect(correct);
    setShowFeedback(true);

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getButtonClass = (option) => {
    if (!showFeedback) {
      return 'btn-number-default hover:border-primary hover:scale-105';
    }
    if (option === count) {
      return 'btn-number-correct';
    }
    if (option === selectedAnswer && !isCorrect) {
      return 'btn-number-incorrect animate-shake';
    }
    return 'btn-number-default opacity-50';
  };

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Question */}
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-8 text-center"
      >
        {QUESTIONS['match-group-to-number']}
      </motion.h2>

      {/* Cubes Display */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="mb-10"
      >
        <CubeBox count={count} size="lg" />
      </motion.div>

      {/* Answer Options - Numbers */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex gap-4 justify-center flex-wrap"
      >
        {options.map((option, index) => (
          <motion.button
            key={option}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1, type: 'spring' }}
            onClick={() => handleAnswer(option)}
            disabled={showFeedback}
            className={getButtonClass(option)}
          >
            {showFeedback && option === count && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center"
              >
                <Check className="w-4 h-4 text-card" />
              </motion.span>
            )}
            {showFeedback && option === selectedAnswer && !isCorrect && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4 text-card" />
              </motion.span>
            )}
            {option}
          </motion.button>
        ))}
      </motion.div>

      {/* Feedback */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MatchGroupToNumber;
